import { EScreenType, getValidationFixability, isValidationFixable, IValidationsModel } from "@kuika/kuika-cl-model";
import type { KuikaBadgeColor } from "../../../components/ui-kit/kuika-badge/kuika-badge";
import type { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import type { ValidationFixItemPayload } from "../mcp-tool-bridge";

/**
 * Shared issues model — pure grouping / counting / fix-payload helpers used by
 * the top-bar Issues panel (issues-panel.tsx). Originally lived in the designer
 * footer, which was removed in KOA-16942 when diagnostics moved to the toolbar;
 * this module is now the only home for that logic.
 */

/**
 * Window events that gate the Issues control's freshness. `VALIDATION_RAN` fires when any run
 * (standalone or generation) lands a validation result; `DESIGN_CHANGED` marks that result
 * stale. Validation is PROJECT-wide, so staleness has two sources: canvas re-renders
 * (frame-provider.renderFrame — screen edits) and successful mutating requests to the other
 * validatable surfaces (workflows, scheduled jobs, API builder, datasource actions, data
 * types, screen create/delete — see validation-dirty-interceptor.ts). Both are plain
 * `window` events.
 */
export const VALIDATION_RAN_EVENT = "kuika:validation-ran";
export const DESIGN_CHANGED_EVENT = "kuika:design-changed";

export enum EIssueType {
  error = "Error",
  warning = "Warning"
}

export type ErrorSourceCategory =
  | "screen"
  | "workflow"
  | "scheduledJob"
  | "apiManagement"
  | "bluetooth"
  | "csharp"
  | "dataType"
  | "datasource"
  | "codeGeneration";

export interface IGroupedErrorItem {
  type: "validation" | "datasource" | "codeGeneration";
  validation?: IValidationsModel;
  datasourceMessage?: string;
  codeGenException?: string;
}

export interface IErrorGroup {
  category: ErrorSourceCategory;
  sourceKey: string;
  displayName: string;
  errorCount: number;
  warningCount: number;
  items: IGroupedErrorItem[];
}

export interface ICategoryMeta {
  label: string;
  color: KuikaBadgeColor;
}

export interface IErrorCounts {
  errorCount: number;
  warningCount: number;
}

export const getCategoryMeta = (category: ErrorSourceCategory): ICategoryMeta => {
  const map: Record<ErrorSourceCategory, ICategoryMeta> = {
    screen: { label: "Screen", color: "blue" },
    workflow: { label: "Process", color: "purple" },
    scheduledJob: { label: "Scheduled Job", color: "pink" },
    apiManagement: { label: "API", color: "green" },
    bluetooth: { label: "BT Datasource", color: "blue" },
    csharp: { label: "C# Datasource", color: "grey" },
    dataType: { label: "Data Type", color: "purple" },
    datasource: { label: "SQL", color: "orange" },
    codeGeneration: { label: "Code Generation", color: "orange" }
  };
  return map[category];
};

export const getCodegenException = (sd: IScreenDesignContextState): string | null => sd.codeGenerationException ?? null;

export const getErrorCounts = (sd: IScreenDesignContextState): IErrorCounts => {
  const validations: IValidationsModel[] = sd.validationErrors || [];
  const dsErrors: string[] = sd.datasourceValidationErrors || [];
  const hasCodeGenException: boolean = !!getCodegenException(sd);

  const errorCount: number =
    validations.filter((v: IValidationsModel) => v.type === EIssueType.error).length +
    dsErrors.length +
    (hasCodeGenException ? 1 : 0);
  const warningCount: number = validations.filter((v: IValidationsModel) => v.type === EIssueType.warning).length;

  return { errorCount, warningCount };
};

export const getGroupedErrors = (sd: IScreenDesignContextState): IErrorGroup[] => {
  const groups: Map<string, IErrorGroup> = new Map<string, IErrorGroup>();
  const validations: IValidationsModel[] = sd.validationErrors || [];
  const dsErrors: string[] = sd.datasourceValidationErrors || [];

  const getGroup = (category: ErrorSourceCategory, sourceKey: string, displayName: string): IErrorGroup => {
    if (!groups.has(sourceKey)) {
      groups.set(sourceKey, {
        category,
        sourceKey,
        displayName,
        errorCount: 0,
        warningCount: 0,
        items: []
      });
    }
    return groups.get(sourceKey)!;
  };

  for (const v of validations) {
    let category: ErrorSourceCategory;
    let name: string;

    if (v.workflowName !== null && v.workflowItem !== null) {
      category = "workflow";
      name = v.workflowName;
    } else if (v.schedulerJobName != null) {
      category = "scheduledJob";
      name = v.schedulerJobName;
    } else if (v.apiManagementName != null) {
      category = "apiManagement";
      name = v.apiManagementName;
    } else if (v.bluetoothDataSourceName !== null) {
      category = "bluetooth";
      name = v.bluetoothDataSourceName;
    } else if (v.csharpDatasourceName !== null) {
      category = "csharp";
      name = v.csharpDatasourceName;
    } else if (v.dataTypeName != null) {
      // A malformed Data Type is generated into the app whether or not a screen references it,
      // so it gets its own source instead of falling into the "Unknown" screen bucket.
      category = "dataType";
      name = v.dataTypeName;
    } else {
      category = "screen";
      name = v.screenName || "Unknown";
    }

    const sourceKey = `${category}:${name}`;
    const group: IErrorGroup = getGroup(category, sourceKey, name);

    if (v.type === EIssueType.error) {
      group.errorCount++;
    } else {
      group.warningCount++;
    }

    group.items.push({ type: "validation", validation: v });
  }

  if (dsErrors.length > 0) {
    const group: IErrorGroup = getGroup("datasource", "datasource:sql", "SQL Errors");
    group.errorCount = dsErrors.length;
    for (const msg of dsErrors) {
      group.items.push({ type: "datasource", datasourceMessage: msg });
    }
  }

  const codegenException: string | null = getCodegenException(sd);
  if (codegenException) {
    const group: IErrorGroup = getGroup("codeGeneration", "codeGeneration:exception", "Code Generation");
    group.errorCount = 1;
    group.items.push({ type: "codeGeneration", codeGenException: codegenException });
  }

  return Array.from(groups.values()).sort((a: IErrorGroup, b: IErrorGroup) => {
    if (a.errorCount > 0 && b.errorCount === 0) return -1;
    if (b.errorCount > 0 && a.errorCount === 0) return 1;
    return b.errorCount + b.warningCount - (a.errorCount + a.warningCount);
  });
};

/**
 * Identity of one issue inside its group, for UI state that must survive re-filtering
 * (the panel's per-row expansion). Derived from the issue, NEVER from its position: the
 * severity tabs change what sits at index N, so a positional key carries an open row's
 * state onto whatever lands in its slot and shows a detail nobody opened. Two genuinely
 * identical validations share a key, which is the right answer for them.
 * React keys are a different concern — those only have to be unique among siblings.
 */
export const getIssueIdentityKey = (sourceKey: string, validation: IValidationsModel): string =>
  [
    sourceKey,
    validation.type,
    validation.code ?? "",
    validation.displayItemId ?? "",
    validation.event ?? "",
    validation.parameterName ?? "",
    validation.actionInstanceId ?? "",
    validation.message
  ].join("|");

/**
 * The compiler diagnostics behind a "Failed to compile C# datasource" validation — the actual
 * Roslyn errors (`CS0103: The name 'x' does not exist…`, with line/column), stamped by the
 * backend when it compiles a C# datasource during project validation. Without them the panel
 * reports only THAT the datasource failed, never why, and the reason is already on the wire.
 * Nullable on the wire, and the array is trimmed of blanks so an empty diagnostic never
 * renders as a bullet with nothing after it.
 */
export const getCompilerErrors = (validation: IValidationsModel): string[] =>
  (validation.csharpErrorMessages ?? []).filter((error: string) => error.trim() !== "");

// ── Issue source trail ──

/**
 * Where a single crumb of an issue's source trail points. `none` is a plain label with no
 * navigation (the trail still names it — knowing the event is useful even when it is not a
 * link). Each member is a named interface because the union is the discriminator, not the
 * shapes.
 */
export interface INoIssueTarget {
  kind: "none";
}

export interface IDisplayItemIssueTarget {
  kind: "displayItem";
  kuikaId: string;
  screenName: string;
  screenType: EScreenType;
}

export interface IScreenIssueTarget {
  kind: "screen";
  screenName: string;
  screenType?: EScreenType;
}

export interface IWorkflowItemIssueTarget {
  kind: "workflowItem";
  workflowItemId: string;
  workflowId: string;
}

export interface IScheduledJobIssueTarget {
  kind: "scheduledJob";
  name: string;
}

export interface IApiIssueTarget {
  kind: "api";
  name: string;
}

export interface IBluetoothIssueTarget {
  kind: "bluetooth";
  datasourceName: string;
  methodName: string | null;
}

export interface ICsharpIssueTarget {
  kind: "csharp";
  name: string;
}

export type IssueSourceTarget =
  | INoIssueTarget
  | IDisplayItemIssueTarget
  | IScreenIssueTarget
  | IWorkflowItemIssueTarget
  | IScheduledJobIssueTarget
  | IApiIssueTarget
  | IBluetoothIssueTarget
  | ICsharpIssueTarget;

export interface IIssueSourceCrumb {
  /** Metadata key, e.g. "Element", "Event", "Screen". */
  label: string;
  value: string;
  target: IssueSourceTarget;
  /** Muted trailing note — "current" on the screen the designer is already on. */
  note?: string;
}

const NO_TARGET: INoIssueTarget = { kind: "none" };

// diId "-1" is the screen root, which has no component name of its own.
const resolveElementName = (diId: string | undefined, displayItem: string): string =>
  diId === "-1" ? "Screen" : displayItem;

/**
 * The per-issue metadata trail: which element, which event, which screen (or process item /
 * job / API / datasource) produced this diagnostic, plus where each part navigates. A
 * diagnostic that only names its owning screen leaves the user hunting for the element, so
 * every issue row carries this trail.
 *
 * An element is a link only while its screen is the one open — loading another screen
 * re-opens the project and drops any selection made before it lands, so cross-screen
 * navigation is the Screen crumb's job and the element is picked up after arriving.
 */
export const buildIssueSourceTrail = (
  sd: IScreenDesignContextState,
  validation: IValidationsModel
): IIssueSourceCrumb[] => {
  const crumbs: IIssueSourceCrumb[] = [];

  if (validation.workflowItem !== null && validation.workflowName !== null) {
    crumbs.push({
      label: "Process Item",
      value: validation.workflowItem,
      target: validation.workflowItemId
        ? { kind: "workflowItem", workflowItemId: validation.workflowItemId, workflowId: validation.workflowId }
        : NO_TARGET
    });
    crumbs.push({ label: "Process", value: validation.workflowName, target: NO_TARGET });
  } else if (validation.schedulerJobName != null) {
    crumbs.push({
      label: "Scheduled Job",
      value: validation.schedulerJobName,
      target: { kind: "scheduledJob", name: validation.schedulerJobName }
    });
  } else if (validation.apiManagementName != null) {
    crumbs.push({
      label: "API",
      value: validation.apiManagementName,
      target: { kind: "api", name: validation.apiManagementName }
    });
  } else if (validation.bluetoothDataSourceName !== null) {
    crumbs.push({
      label: "BT Datasource",
      value: validation.bluetoothDataSourceName,
      target: { kind: "bluetooth", datasourceName: validation.bluetoothDataSourceName, methodName: null }
    });
    if (validation.bluetoothMethodName !== null) {
      crumbs.push({
        label: "BT Method",
        value: validation.bluetoothMethodName,
        target: {
          kind: "bluetooth",
          datasourceName: validation.bluetoothDataSourceName,
          methodName: validation.bluetoothMethodName
        }
      });
    }
  } else if (validation.csharpDatasourceName !== null) {
    crumbs.push({
      label: "C# Datasource",
      value: validation.csharpDatasourceName,
      target: { kind: "csharp", name: validation.csharpDatasourceName }
    });
  } else if (validation.dataTypeName != null) {
    crumbs.push({ label: "Data Type", value: validation.dataTypeName, target: NO_TARGET });
  } else {
    const diId: string | undefined = validation.displayItemId?.toString();
    const screenType: EScreenType | undefined = sd.screenList?.find((s) => s.name === validation.screenName)
      ?.publicMetaData?.screenType;
    const isCurrentScreen: boolean = sd.sdName === validation.screenName;

    if (validation.displayItem) {
      const canSelect: boolean = isCurrentScreen && !!diId && screenType !== undefined;
      crumbs.push({
        label: "Element",
        value: resolveElementName(diId, validation.displayItem),
        target: canSelect
          ? { kind: "displayItem", kuikaId: diId!, screenName: validation.screenName, screenType: screenType! }
          : NO_TARGET
      });
    }
    if (validation.event) {
      crumbs.push({ label: "Event", value: validation.event, target: NO_TARGET });
    }
    if (validation.screenName) {
      crumbs.push({
        label: screenType === EScreenType.Report ? "Report" : "Screen",
        value: validation.screenName,
        target: isCurrentScreen ? NO_TARGET : { kind: "screen", screenName: validation.screenName, screenType },
        note: isCurrentScreen ? "current" : undefined
      });
    }
  }

  // A blank name is worse than no crumb — the backend leaves these empty on some sources.
  return crumbs.filter((crumb: IIssueSourceCrumb) => crumb.value !== "");
};

// Map a fixable validation to the structured wire item the AI-fix server expects.
// Returns null for non-fixable validations (no code / not yet supported).
export const buildValidationFixItem = (
  sd: IScreenDesignContextState,
  validation: IValidationsModel
): ValidationFixItemPayload | null => {
  const fixability = getValidationFixability(validation.code);
  if (!fixability || !fixability.fixable || !validation.code) return null;
  const screenId = sd.screenList?.find((s) => s.name === validation.screenName)?.id;
  return {
    code: validation.code,
    category: fixability.category,
    screenName: validation.screenName,
    screenId: screenId !== undefined && screenId !== null ? screenId.toString() : undefined,
    displayItemId: validation.displayItemId,
    kuikaId:
      validation.displayItemId !== undefined && validation.displayItemId !== null
        ? validation.displayItemId.toString()
        : undefined,
    event: validation.event || undefined,
    parameterName: validation.parameterName,
    actionInstanceId: validation.actionInstanceId,
    message: validation.message,
    type: validation.type
  };
};

// Count of AI-fixable validations — drives the "Fix all with AI" button.
export const getFixableValidationCount = (sd: IScreenDesignContextState): number => {
  return (sd.validationErrors || []).filter((v: IValidationsModel) => isValidationFixable(v)).length;
};
